import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Download, RotateCcw, Share2, Calendar } from 'lucide-react';
import { dimensions } from '../data/dimensions';

const AssessmentComplete = ({ progress, onRestart }) => {
  const totalPostures = dimensions.reduce((total, dim) => total + dim.postures.length, 0);
  const completedCount = progress.completedPostures.length;
  const completionPercentage = Math.round((completedCount / totalPostures) * 100);

  const handleDownloadResults = () => {
    // Create a summary of the assessment
    const results = {
      completionDate: new Date().toISOString(),
      totalPostures,
      completedPostures: completedCount,
      completionPercentage,
      dimensions: dimensions.map(dim => ({
        id: dim.id,
        name: dim.name,
        postures: dim.postures.map(posture => ({
          id: posture.id,
          name: posture.name,
          completed: progress.completedPostures.includes(posture.id),
          recordingExists: !!progress.recordings[posture.id]
        }))
      }))
    };

    const dataStr = JSON.stringify(results, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `7-dimensions-assessment-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getDimensionStats = () => {
    return dimensions.map(dim => {
      const dimensionPostures = dim.postures.length;
      const completedInDimension = dim.postures.filter(p => 
        progress.completedPostures.includes(p.id)
      ).length;
      const percentage = Math.round((completedInDimension / dimensionPostures) * 100);
      
      return {
        ...dim,
        completed: completedInDimension,
        total: dimensionPostures,
        percentage
      };
    });
  };

  const dimensionStats = getDimensionStats();

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center p-6">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          {/* Success Icon */}
          <motion.div
            className="mb-8"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center mb-6">
              <Trophy className="w-12 h-12 text-white" />
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            className="text-4xl md:text-5xl font-light text-dark-50 mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Assessment Complete!
          </motion.h1>
          
          <motion.p
            className="text-xl text-dark-300 mb-8"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Congratulations on completing your 7 Dimensions assessment
          </motion.p>

          {/* Overall Stats */}
          <motion.div
            className="grid md:grid-cols-3 gap-6 mb-12"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="card text-center">
              <div className="text-3xl font-bold text-accent-400 mb-2">
                {completedCount}
              </div>
              <div className="text-dark-300">Postures Completed</div>
            </div>
            
            <div className="card text-center">
              <div className="text-3xl font-bold text-accent-400 mb-2">
                {completionPercentage}%
              </div>
              <div className="text-dark-300">Completion Rate</div>
            </div>
            
            <div className="card text-center">
              <div className="text-3xl font-bold text-accent-400 mb-2">
                7
              </div>
              <div className="text-dark-300">Dimensions Assessed</div>
            </div>
          </motion.div>

          {/* Dimension Breakdown */}
          <motion.div
            className="mb-12"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h2 className="text-2xl font-light text-dark-100 mb-6">
              Dimension Breakdown
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dimensionStats.map((dim, index) => (
                <motion.div
                  key={dim.id}
                  className="card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.7 + index * 0.1 }}
                >
                  <h3 className="font-medium text-dark-100 mb-2 text-left">
                    {dim.name}
                  </h3>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-dark-400">
                      {dim.completed} of {dim.total} postures
                    </span>
                    <span className="text-sm font-medium text-accent-400">
                      {dim.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-dark-700 rounded-full h-2">
                    <motion.div
                      className="h-full bg-gradient-to-r from-accent-500 to-accent-600 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${dim.percentage}%` }}
                      transition={{ duration: 0.8, delay: 0.8 + index * 0.1 }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <button
              onClick={handleDownloadResults}
              className="btn-primary flex items-center space-x-2 px-6 py-3"
            >
              <Download className="w-5 h-5" />
              <span>Download Results</span>
            </button>
            
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: '7 Dimensions Assessment Complete',
                    text: `I just completed my 7 Dimensions posture assessment with ${completionPercentage}% completion!`,
                    url: window.location.href
                  });
                }
              }}
              className="btn-secondary flex items-center space-x-2 px-6 py-3"
            >
              <Share2 className="w-5 h-5" />
              <span>Share Results</span>
            </button>
            
            <button
              onClick={onRestart}
              className="btn-secondary flex items-center space-x-2 px-6 py-3"
            >
              <RotateCcw className="w-5 h-5" />
              <span>Start New Assessment</span>
            </button>
          </motion.div>

          {/* Completion Date */}
          <motion.div
            className="flex items-center justify-center space-x-2 text-dark-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <Calendar className="w-4 h-4" />
            <span className="text-sm">
              Completed on {new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default AssessmentComplete;
